<script>
import { mapState } from 'pinia';

import { useUserStore } from '../../../store/useUserStore.js';
import { getPost } from '../api/posts';
import moment from 'moment';
import queryString from '../lib/query-string';
import toHTML from 'linkifyjs/html';

export default {
  directives: {
    margin: require('../directives/margins').margin,
    marginTop: require('../directives/margins').marginTop,
    marginRight: require('../directives/margins').marginRight,
    marginBottom: require('../directives/margins').marginBottom,
    marginLeft: require('../directives/margins').marginLeft,
  },
  components: {
    VueSimpleSpinner: require('../vue-simple-spinner').default,
    CpCommentsPost: require('../comments/box-comments').default,
    CpPostActions: require('../social-feed/posts/opinion-post').default,
    CpSearchResult: require('../explore-campaigns/result').default,
    CpSocialFeedPetitionPost: require('./posts/petition-post').default,
    CpSocialFeedNewsPost: require('./posts/news-post').default,
    CpSocialFeedRacePost: require('./posts/race-post').default,
    CpSocialFeedBlogPost: require('./posts/blog-post').default,
    CpSocialFeedLobbyPost: require('./posts/lobby-post').default,
    CpOpinionsItem: require('../opinions/opinions-item').default,
    CpSocialFeedPollPost: require('./posts/poll-post').default,
    CpSocialFeedCandidatePost: require('./posts/candidate-post').default,
    CpSocialFeedCustomContent: require('./posts/CustomContent').default,
    CpSharePostItem: require('./posts/share-post-item').default,
    CpGroup: require('../groups/group-card').default,
    CpEventCard: require('../events/event-card.vue').default,
    CpSocialFeedLivestreamPost: require('./posts/livestream-post').default,
    capitalizeFirstLetter: require('../mixins/utils.js').default,
    CpPopularCampaigns: require('../explore-campaigns/components/PopularCampagins').default,
    CpSocialFeedStartCampaignPost: require('./posts/StartCampaignPost.vue').default,
  },
  props: {
    user: Object,
  },
  data() {
    return {
      loading: true,
      error: null,
    };
  },
  methods: {
    async fetch() {
      try {
        const params = queryString.parse(location.search);

        this.post = await getPost({
          type: this.capitalizeFirstLetter(params.type),
          id: params.id,
          ...(params.preview && { preview: params.preview === '1' }),
        });
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetch();
  },
  computed: {
    authUser() {
      return this.$root.authUser;
    },
    ...mapState(useUserStore, ['isUserLogin']),
  },
};
</script>

<template>
  <div>
    <div v-if="loading" class="heading-4 loading container">
      <vue-simple-spinner v-margin="1" />
    </div>
    <div v-else>
      <CpSocialFeedCustomContent v-if="post.type_post == 'CustomContent'" :post="post" :explore="true" />
      <cp-opinions-item v-if="post.type == 'StatusPost'" :data="post" :type="'opinion'" :explore="true" key="opinion" />
      <cp-opinions-item v-else-if="post.type === 'GroupOpinion'" :data="post" :type="'group-opinions'" :explore="true" key="group_opinion" />
      <cp-search-result v-else-if="post.type === 'Crowdpac'" :result="post" :explore="true" :feed="true" key="campaign" />
      <cp-social-feed-petition-post v-else-if="post.type === 'Petition'" key="petition" :petition="post" :explore="true" :user="user" />
      <cp-social-feed-news-post v-else-if="post.type === 'News'" key="news" :news="post" :explore="true" />
      <cp-social-feed-race-post v-else-if="post.type === 'Election'" key="race" :race="post" :explore="true" />
      <cp-social-feed-blog-post v-else-if="post.type === 'Blog'" key="blog" :blog="post" :explore="true" />
      <cp-social-feed-lobby-post
        v-else-if="post.type === 'Lobby'"
        key="lobby"
        :lobby="post"
        :for-share="false"
        :explore="true"
        :iconState="true"
        :user="user"
      />
      <cp-share-post-item v-else-if="post.type === 'PostShared'" key="post_shared" :post-shared="post" />
      <cp-social-feed-poll-post v-else-if="post.type_post === 'Poll'" key="poll" :poll="post" :explore="true" />
      <cp-social-feed-candidate-post v-else-if="post.type === 'Candidate'" key="candidate" :candidate="post" :explore="true" :iconState="true" />
      <cp-group v-if="post.type_post == 'Group'" :group="post" :current-user="user" :type="post.type" />
      <div v-if="post.type_post == 'GroupEvent'">
        <cp-social-feed-livestream-post
          v-if="post.archive_url && post.status == 'shared'"
          :key="post.video_type"
          :data="post"
          :playback="post.archive_url"
          :title="post.title"
          :type="'event'"
        />
        <cp-event-card v-else :event="post" :for-share="true" :explore="true" />
      </div>
      <cp-social-feed-livestream-post
        v-else-if="post.type_post === 'Livestream'"
        :key="post.video_type"
        :data="post"
        :playback="post.archive_url"
        :title="post.title"
        :type="post.type"
      />
      <div v-if="post.type === 'StatusPost' || post.type === 'News' || post.type_post === 'CustomContent'" class="bottom-items" v-margin-top="1">
        <CpPopularCampaigns />
        <CpSocialFeedStartCampaignPost class="start-campaign-post" v-margin-top="1" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.container {
  display: flex;
  flex: 1;
  margin-bottom: var(--gutter);
}
.loading {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
}
.post-wrapper {
  display: flex;
  flex: 1;
  flex-direction: column;
  border: 1px solid var(--extra-light-gray);
  padding: var(--gutter);
  margin-top: var(--gutter);
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.bottom-items {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.start-campaign-post {
  max-width: 300px;
}
</style>
